import { useMemo } from "react"
import PropTypes from "prop-types"
import { compact, map } from "lodash"
import AssetsHeader from "components/assets-header"
import AssetAnalyticsTable from "modules/asset-analytics-table"
import { ACTIONS } from "modules/ai-summary/constants"
import { NORMALIZATION } from "helpers/segments"
import { DATE, FILTER_ON_GROUP, PERIOD, FILTER } from "helpers/shapes"
import AISummarizer from "modules/ai-summary"
import { Divider } from "@mui/material"
import { VIEW_TYPES } from "helpers/base"
import { usePositionSetMetadata } from "resources/position-sets"
import { LONG } from "resources/position-sets/composition"

import "./styles.scss"

function AssetAnalytics({
  account,
  addNotification,
  availableRollups,
  base,
  baseSignature,
  currentGrouping,
  datesAdjusted,
  endDate,
  performanceStartDate,
  earliestDate,
  filter,
  filterOns,
  filterScope,
  filtersVisible,
  firstDate,
  includeNotHeld,
  isActive,
  isBenchmarkUnfiltered,
  isOpPro,
  latestDate,
  modelId,
  normalization,
  period,
  portfolioId,
  segment,
  setFilterScope,
  setGrouping,
  setNormalization,
  setPeriod,
  showNotHeld,
  toggleFilters,
  toggleShowNotHeld,
  viewType,
  setExpansionMode,
  isExpanded,
  hasUnexpandedComposites,
  portfolioName,
  hasUnsupportedContributionFilter,
  currency,
  isAiTermsAccepted,
  addedFilters,
  updateFilters,
  isPortfolioFiltersLoading,
  isHasUnexpandedCompositesLoading,
}) {
  const assetType = "PORTFOLIO"
  const selectedBase = viewType === VIEW_TYPES.ACTIVE ? base : undefined
  const portfolioPositionSet = useMemo(() => isBenchmarkUnfiltered ? { type: assetType, id: portfolioId } : undefined, [isBenchmarkUnfiltered, assetType, portfolioId])
  const [portfolioMetadata] = usePositionSetMetadata(modelId, portfolioPositionSet, latestDate)
  const customLeftButtons = compact([
    isAiTermsAccepted && <>
      <Divider orientation="vertical" flexItem />
      <AISummarizer {...{
        action: ACTIONS.SECURITY_SUMMARY,
        account,
        modelId,
        resourceId: portfolioId,
        resourceType: assetType,
        ...(isActive && { base: selectedBase }),
        startDate: performanceStartDate,
        endDate,
      }}
      />
    </>,
  ])

  return (
    <div className="modules_asset-analytics">
      <AssetsHeader
        {...{
          account,
          addNotification,
          periodAdjusted: datesAdjusted,
          availableRollups,
          currentGrouping,
          datesWithLabels: true,
          earliestDate,
          endDate,
          filter,
          filterOns,
          filterScope,
          filtersVisible,
          firstDate,
          isActive,
          latestDate,
          modelId,
          normalization,
          period,
          assetId: portfolioId,
          assetType,
          setFilterScope,
          baseType: base?.type,
          setGrouping,
          setNormalization,
          setPeriod,
          showFilterScope: true,
          showFilterScopeBasisChip: isBenchmarkUnfiltered,
          showGrossExposureNote: portfolioMetadata?.compositionType !== LONG,
          showNotHeld,
          startDate: performanceStartDate,
          toggleFilters,
          toggleShowNotHeld,
          setExpansionMode,
          isExpanded,
          hasUnexpandedComposites,
          hasUnsupportedContributionFilter,
          currency,
          customLeftButtons,
          addedFilters,
          updateFilters,
          isPortfolioFiltersLoading,
          isHasUnexpandedCompositesLoading,
        }}
      />
      <AssetAnalyticsTable
        {...{
          account,
          base,
          baseSignature,
          currentGrouping,
          endDate,
          expand: isExpanded,
          includeNotHeld,
          isActive,
          isOpPro,
          modelId,
          performanceStartDate,
          assetId: portfolioId,
          assetType: "PORTFOLIO",
          assetName: portfolioName,
          segment,
          showNotHeld,
          viewType,
        }}
      />
    </div>
  )
}

AssetAnalytics.propTypes = {
  account: PropTypes.string.isRequired,
  addNotification: PropTypes.func,
  availableRollups: PropTypes.array.isRequired,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  currentGrouping: PropTypes.object,
  datesAdjusted: PropTypes.bool,
  earliestDate: DATE,
  performanceStartDate: DATE,
  endDate: DATE,
  period: PERIOD.isRequired,
  filter: PropTypes.object,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  filterScope: PropTypes.string,
  filtersVisible: PropTypes.bool.isRequired,
  firstDate: PropTypes.string,
  includeNotHeld: PropTypes.bool,
  isActive: PropTypes.bool,
  isBenchmarkUnfiltered: PropTypes.bool,
  isOpPro: PropTypes.bool.isRequired,
  latestDate: DATE,
  modelId: PropTypes.string.isRequired,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  portfolioId: PropTypes.string.isRequired,
  segment: PropTypes.object,
  setFilterScope: PropTypes.func.isRequired,
  setGrouping: PropTypes.func.isRequired,
  setNormalization: PropTypes.func.isRequired,
  setPeriod: PropTypes.func.isRequired,
  showNotHeld: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  toggleShowNotHeld: PropTypes.func.isRequired,
  viewType: PropTypes.string,
  setExpansionMode: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  hasUnexpandedComposites: PropTypes.bool,
  portfolioName: PropTypes.string.isRequired,
  hasUnsupportedContributionFilter: PropTypes.bool.isRequired,
  currency: PropTypes.string,
  isAiTermsAccepted: PropTypes.bool.isRequired,
  addedFilters: PropTypes.arrayOf(FILTER),
  updateFilters: PropTypes.func,
  isPortfolioFiltersLoading: PropTypes.bool.isRequired,
  isHasUnexpandedCompositesLoading: PropTypes.bool.isRequired,
}

export default AssetAnalytics
